import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterFieldDef } from '../../routes/filterMaps';

function parseUrlParam(raw: string, def: FilterFieldDef): unknown {
  switch (def.type) {
    case 'number':
      return Number(raw);
    case 'boolean':
      return raw === 'true';
    case 'comma-separated-strings':
      return raw ? raw.split(',').filter(Boolean) : [];
    case 'comma-separated-numbers':
      return raw ? raw.split(',').filter(Boolean).map(Number) : [];
    case 'string':
    default:
      return raw;
  }
}

export function serializeValue(value: unknown, def: FilterFieldDef): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  switch (def.type) {
    case 'string':
      return String(value);
    case 'number':
      return String(value);
    case 'boolean':
      return String(value);
    case 'comma-separated-strings':
      if (Array.isArray(value) && value.length > 0) {
        return value.join(',');
      }
      return undefined;
    case 'comma-separated-numbers':
      if (Array.isArray(value) && value.length > 0) {
        return value.join(',');
      }
      return undefined;
    default:
      return String(value);
  }
}

function buildFilterFromUrl<T extends object>(
  params: URLSearchParams,
  defaultFilter: T,
  filterFields: FilterFieldDef[],
  transform?: { deserialize?: (params: URLSearchParams, defaultFilter: T) => Partial<T> },
): T {
  const fromUrl: Record<string, unknown> = {};

  const custom = transform?.deserialize?.(params, defaultFilter);
  if (custom) {
    Object.assign(fromUrl, custom);
  }

  for (const def of filterFields) {
    if (fromUrl[def.field] !== undefined) continue;
    const raw = params.get(def.param);
    if (raw !== null) {
      fromUrl[def.field] = parseUrlParam(raw, def);
    }
  }

  if (Object.keys(fromUrl).length === 0) {
    return defaultFilter;
  }
  return { ...defaultFilter, ...fromUrl };
}

export interface UseUrlListFilterOptions<T> {
  transform?: {
    deserialize?: (params: URLSearchParams, defaultFilter: T) => Partial<T>;
    serialize?: (filter: T) => Record<string, string | undefined>;
  };
}

export function useUrlListFilter<T extends object>(
  defaultFilter: T,
  filterFields: FilterFieldDef[],
  options?: UseUrlListFilterOptions<T>,
): [T, (value: T | ((prev: T) => T)) => void, () => void, (value: T | ((prev: T) => T)) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterRef = useRef<T>(undefined as unknown as T);

  const filter = useMemo<T>(
    () => buildFilterFromUrl(searchParams, defaultFilter, filterFields, options?.transform),
    [searchParams, defaultFilter, filterFields, options?.transform],
  );
  useLayoutEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  const pushFilterToUrl = useCallback(
    (next: T) => {
      const serialized: Record<string, string | undefined> = {};
      for (const def of filterFields) {
        const value = (next as Record<string, unknown>)[def.field];
        serialized[def.param] = serializeValue(value, def);
      }
      const custom = options?.transform?.serialize?.(next);
      if (custom) {
        Object.assign(serialized, custom);
      }
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(serialized)) {
            if (val !== undefined) {
              nextParams.set(key, val);
            } else {
              nextParams.delete(key);
            }
          }
          return nextParams;
        },
        { replace: true },
      );
    },
    [filterFields, options?.transform],
  );

  const setFilter = useCallback(
    (value: T | ((prev: T) => T)) => {
      const next = typeof value === 'function' ? (value as (prev: T) => T)(filterRef.current) : value;
      pushFilterToUrl(next);
    },
    [pushFilterToUrl],
  );

  const silentSetFilter = useCallback(
    (value: T | ((prev: T) => T)) => {
      const current = filterRef.current;
      const next = typeof value === 'function' ? (value as (prev: T) => T)(current) : value;
      if (next !== current) {
        pushFilterToUrl(next);
      }
    },
    [pushFilterToUrl],
  );

  const resetFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const def of filterFields) {
          next.delete(def.param);
        }
        const custom = options?.transform?.serialize?.(defaultFilter);
        if (custom) {
          for (const key of Object.keys(custom)) {
            next.delete(key);
          }
        }
        return next;
      },
      { replace: true },
    );
  }, [filterFields, options?.transform, defaultFilter]);

  return [filter, setFilter, resetFilter, silentSetFilter];
}
